import * as React from "react";
import { Box, styled } from "@mui/material";
import { isValidNumericInput } from "@/utils/commonFunctions";
import { gray600, gray650 } from "@/constants/colors";

const InputElement = styled("input")(
  () => `
  width: 2.75rem;
  aspect-ratio: 1 / 1;
  font-size: 0.875rem;
  font-weight: 400;
  line-height: 1.125;
  border-radius: 0.25rem;
  text-align: center;
  color: ${gray600};
  background-color: transparent;
  border: 1px solid ${gray650};
  box-shadow: none;
  &:focus {
    outline: none;
    border-color: ${gray600};
  }
`
);

interface Props {
  length: number;
  value: string;
  onChange: React.Dispatch<React.SetStateAction<string>>;
}

const OTPInput: React.FC<Props> = ({ length, value, onChange }) => {
  const inputRefs = React.useRef<HTMLInputElement[]>(new Array(length).fill(null));

  const focusInput = (targetIndex: number) => {
    const targetInput = inputRefs.current[targetIndex];
    targetInput.focus();
  };

  const selectInput = (targetIndex: number) => {
    const targetInput = inputRefs.current[targetIndex];
    targetInput.select();
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>, currentIndex: number) => {
    switch (event.key) {
      case "ArrowUp":
      case "ArrowDown":
      case " ":
        event.preventDefault();
        break;
      case "ArrowLeft":
        event.preventDefault();
        if (currentIndex > 0) {
          focusInput(currentIndex - 1);
          selectInput(currentIndex - 1);
        }
        break;
      case "ArrowRight":
        event.preventDefault();
        if (currentIndex < length - 1) {
          focusInput(currentIndex + 1);
          selectInput(currentIndex + 1);
        }
        break;
      case "Delete":
        event.preventDefault();
        onChange(prevOtp => {
          const otp = prevOtp.slice(0, currentIndex) + prevOtp.slice(currentIndex + 1);
          return otp;
        });

        break;
      case "Backspace":
        event.preventDefault();
        if (currentIndex > 0) {
          focusInput(currentIndex - 1);
          selectInput(currentIndex - 1);
        }

        onChange(prevOtp => {
          const otp = prevOtp.slice(0, currentIndex) + prevOtp.slice(currentIndex + 1);
          return otp;
        });
        break;

      default:
        break;
    }
  };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>, currentIndex: number) => {
    const currentValue = event.target.value;
    if (!isValidNumericInput(currentValue)) {
      return;
    }

    let indexToEnter = 0;

    while (indexToEnter <= currentIndex) {
      if (inputRefs.current[indexToEnter].value && indexToEnter < currentIndex) {
        indexToEnter += 1;
      } else {
        break;
      }
    }
    onChange(prev => {
      const otpArray = prev.split("");
      const lastValue = currentValue[currentValue.length - 1];
      otpArray[indexToEnter] = lastValue;
      return otpArray.join("");
    });
    if (currentValue !== "") {
      if (currentIndex < length - 1) {
        focusInput(currentIndex + 1);
      }
    }
  };

  const handleClick = (event: React.MouseEvent<HTMLInputElement, MouseEvent>, currentIndex: number) => {
    selectInput(currentIndex);
  };

  const handlePaste = (event: React.ClipboardEvent<HTMLInputElement>, currentIndex: number) => {
    event.preventDefault();
    const clipboardData = event.clipboardData;

    if (clipboardData.types.includes("text/plain")) {
      let pastedText = clipboardData.getData("text/plain");
      pastedText = pastedText.substring(0, length).trim();

      if (!isValidNumericInput(pastedText)) {
        return;
      }

      let indexToEnter = 0;

      while (indexToEnter <= currentIndex) {
        if (inputRefs.current[indexToEnter].value && indexToEnter < currentIndex) {
          indexToEnter += 1;
        } else {
          break;
        }
      }

      const otpArray = value.split("");

      for (let i = indexToEnter; i < length; i += 1) {
        const lastValue = pastedText[i - indexToEnter] ?? " ";
        otpArray[i] = lastValue;
      }

      onChange(otpArray.join(""));
    }
  };

  return (
    <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
      {new Array(length).fill(null).map((_, index) => (
        <InputElement
          key={index}
          aria-label={`Digit ${index + 1} of OTP`}
          ref={ele => {
            inputRefs.current[index] = ele!;
          }}
          onKeyDown={event => handleKeyDown(event, index)}
          onChange={event => handleChange(event, index)}
          onClick={event => handleClick(event, index)}
          onPaste={event => handlePaste(event, index)}
          value={value[index] ?? ""}
          inputMode="numeric"
          pattern="[0-9]*"
          maxLength={1}
          type="tel"
        />
      ))}
    </Box>
  );
};

export default OTPInput;
